import React, {useEffect, useRef, useState, useCallback, memo, useMemo} from "react";

type ActionSelectProps = {
  label: string;
  children: React.ReactNode;
};

export const ActionSelect: React.FC<ActionSelectProps> = memo(({ label, children }) => {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Flatten children, accounting for React Fragments
  const flattenChildren = useCallback((nodes: React.ReactNode): React.ReactElement[] => {
    const flat: React.ReactElement[] = [];
    React.Children.forEach(nodes, (child) => {
      if (React.isValidElement(child)) {
        if (child.type === React.Fragment) {
          flat.push(...flattenChildren(child.props.children));
        } else {
          flat.push(child);
        }
      }
    });
    return flat;
  }, []);

  const items = flattenChildren(children);

  // Close dropdown on outside click, using capture phase so stopPropagation in
  // child rows (e.g. PaginatedSelector) doesn't prevent this from firing.
  useEffect(() => {
    if (!show) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [show]);

  // Action callback
  const action = useCallback(
      (callback: () => void, keepOpen?: boolean) => {
        callback();
        if (!keepOpen) setShow(false);
      },
      []
  );

  const visibleItems = useMemo(() => items.filter(i => !i.props.hide), [items]);

  if (visibleItems.length === 0) {
    return null;
  }



  return (
      <div ref={ref} className="relative overflow-visible">
        <button
            type="button"
            className="w-full min-w-fit h-10 text-white text-sm rounded-md shadow-lg flex items-center gap-6 justify-center text-center hover:bg-opacity-80 transition-opacity bg-blue"
            onClick={() => setShow((prev) => !prev)}
        >
          {label}
        </button>

        {show && (
            <div className="absolute left-0 right-0 top-12 bg-white rounded-md shadow-2xl p-4 z-50 flex flex-col gap-4">
              {visibleItems.map((item, index) => (
                  <div key={index} className="">
                    <button
                        onClick={() => action(item.props.onSelect, item.props.keepOpen)}
                        className="text-center w-full"
                    >
                      {item.props.label}
                    </button>
                  </div>
              ))}
            </div>
        )}
      </div>
  );
});

ActionSelect.displayName = "ActionSelect"

type ActionProps = {
  label: string;
  onSelect: () => void;
  keepOpen?: boolean;
  hide?: boolean;
};

// Memoized Action component
export const Action: React.FC<ActionProps> = memo(() => null);

Action.displayName = "Action"